import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, User, DollarSign, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Appointment } from './MasterDashboard/MasterDashboard';

interface MasterCalendarProps {
  appointments: Appointment[];
}

export const MasterCalendar: React.FC<MasterCalendarProps> = ({ appointments }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getAppointmentsForDate = (date: Date) => {
    if (!date) return [];
    
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.appointment_date);
      return appointmentDate.toDateString() === date.toDateString();
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('uk-UA', {
      month: 'long',
      year: 'numeric'
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

  // Calculate statistics
  const todayAppointments = getAppointmentsForDate(new Date());
  const totalRevenue = appointments
    .filter(apt => apt.status === 'completed')
    .reduce((sum, apt) => sum + (apt.total_price || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">Сьогодні</p>
              <p className="text-2xl font-bold text-blue-900">{todayAppointments.length}</p>
            </div>
            <CalendarIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Всього записів</p>
              <p className="text-2xl font-bold text-green-900">{appointments.length}</p>
            </div>
            <User className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600">Заробіток</p>
              <p className="text-2xl font-bold text-purple-900">₴{totalRevenue}</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-6">
        <button 
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => navigateMonth('prev')}
        >
          <CalendarIcon className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold">{formatDate(currentMonth)}</h3>
        <button 
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => navigateMonth('next')}
        >
          <CalendarIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {/* Week day headers */}
        {weekDays.map((day, index) => (
          <div key={index} className="text-center text-sm font-medium text-gray-600 py-2">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="aspect-square"></div>;
          }

          const dayAppointments = getAppointmentsForDate(date);
          const isSelected = selectedDate?.toDateString() === date.toDateString();
          const isToday = new Date().toDateString() === date.toDateString();
          const hasAppointments = dayAppointments.length > 0;

          return (
            <div
              key={index}
              className={`aspect-square border rounded-lg p-2 cursor-pointer transition-colors ${
                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              } ${isToday ? 'bg-yellow-50' : ''} ${hasAppointments ? 'bg-green-50' : ''}`}
              onClick={() => setSelectedDate(date)}
            >
              <div className="text-sm font-medium">{date.getDate()}</div>
              {hasAppointments && (
                <div className="flex justify-center mt-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Date Appointments */}
      {selectedDate && (
        <div className="border-t pt-4">
          <h4 className="text-lg font-semibold mb-4">
            Записи на {selectedDate.toLocaleDateString('uk-UA', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </h4>
          {getAppointmentsForDate(selectedDate).length > 0 ? (
            <div className="space-y-3">
              {getAppointmentsForDate(selectedDate).map(appointment => (
                <div key={appointment.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">
                          {new Date(appointment.appointment_date).toLocaleTimeString('uk-UA', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({appointment.duration_minutes || 0} хв)
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{appointment.client?.name || 'Unknown Client'}</span>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          {appointment.service?.name || 'Unknown Service'}
                        </div>
                        
                        {appointment.notes && (
                          <div className="text-sm text-gray-500 italic">
                            {appointment.notes}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(appointment.status)}`}>
                        {getStatusIcon(appointment.status)}
                        <span>
                          {appointment.status === 'confirmed' ? 'Підтверджено' :
                           appointment.status === 'pending' ? 'Очікує' :
                           appointment.status === 'completed' ? 'Завершено' :
                           appointment.status === 'cancelled' ? 'Скасовано' : appointment.status}
                        </span>
                      </div>
                      
                      <div className="text-lg font-semibold text-green-600">
                        ₴{appointment.total_price || 0}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Немає записів на цю дату
            </p>
          )}
        </div>
      )}
    </div>
  );
};
